import { motion } from "framer-motion";
import { Reveal, Eyebrow, EASE } from "@/components/Reveal";
import { scrollToId } from "@/lib/scroll";

const REASONS = [
  {
    n: "01",
    title: "AIGP-certified leadership",
    desc: "Your governance is designed and led by a certified AI governance professional (IAPP AIGP) — not repackaged generalist decks.",
  },
  {
    n: "02",
    title: "Three disciplines, one partner",
    desc: "AI governance, PMO transformation and cybersecurity are engineered together, so controls enable velocity instead of fighting it.",
  },
  {
    n: "03",
    title: "Board-ready assurance",
    desc: "Every engagement leaves behind evidence — metrics, audit trails and reporting your board, auditors and regulators can actually use.",
  },
  {
    n: "04",
    title: "Vendor-neutral by design",
    desc: "We work inside your stack, your constraints and your culture. No tooling resale, no lock-in, no conflicts of interest.",
  },
];

export default function WhyAiCendor() {
  return (
    <section id="why" className="relative bg-surface/40 py-28 lg:py-36">
      <div className="mx-auto max-w-7xl px-6 lg:px-10">
        <div className="grid gap-16 lg:grid-cols-12">
          <div className="lg:col-span-5">
            <div className="lg:sticky lg:top-32">
              <Reveal>
                <Eyebrow>Why AiCendor</Eyebrow>
              </Reveal>
              <Reveal delay={0.1}>
                <h2 className="mt-8 font-display text-4xl font-extrabold tracking-tighter sm:text-5xl">
                  Certified where it counts. Partnered where it matters.
                </h2>
              </Reveal>
              <Reveal delay={0.2}>
                <p className="mt-8 max-w-md text-sm leading-relaxed text-slate-400 sm:text-base">
                  Anyone can promise &ldquo;responsible AI&rdquo;. We bring the certification, the
                  delivery record and the security depth to make it stand up in front of your board.
                </p>
              </Reveal>
              <Reveal delay={0.3}>
                <button
                  data-testid="why-cta-button"
                  onClick={() => scrollToId("contact")}
                  className="btn-sweep mt-10 border border-white/25 px-8 py-4 font-mono text-xs uppercase tracking-[0.22em] text-white"
                >
                  Start the conversation
                </button>
              </Reveal>
            </div>
          </div>

          <div className="lg:col-span-7">
            {REASONS.map((r, i) => (
              <motion.div
                key={r.n}
                data-testid="why-row"
                initial={{ opacity: 0, y: 32 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, amount: 0.3 }}
                transition={{ duration: 0.8, delay: i * 0.08, ease: EASE }}
                className="group border-t border-white/10 py-10 transition-[padding] duration-500 last:border-b hover:pl-4"
              >
                <div className="flex items-start gap-6 lg:gap-10">
                  <span className="mt-2 font-mono text-xs tracking-[0.3em] text-accent">{r.n}</span>
                  <div>
                    <h3 className="font-display text-2xl font-bold tracking-tight transition-colors duration-300 group-hover:text-accent lg:text-3xl">
                      {r.title}
                    </h3>
                    <p className="mt-3 max-w-xl text-sm leading-relaxed text-slate-400 sm:text-base">{r.desc}</p>
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}
